import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { assignOwner } from '../api/devices'
import { unassignedTallyQuery, type UnassignedBandData, type UnassignedTally } from '../api/inventoree'
import { toast } from '../ui/notify'
import { exact, fromNow, stamp } from './format'
import { AssignModal } from './UnassignedFirst'
import './UnassignedBand.css'

/**
 * BANDA ROSIE DIN CAPUL LISTELOR INVENTOREE - statiile fara utilizator.
 *
 * Bogdan, 5 octombrie 2026: "o linie rosie cu 10 statii, ultima cand a fost
 * descoperita, si doar la chevron expand detaliile si Assign". Coordonatoarea:
 * "sus in header cate sunt, fac si sus rosu".
 *
 *  - banda (`band`) sta imediat deasupra tabelului: "N stations without a
 *    user", "last discovered <cand>" (`MAX(DataAdaugare)`), chevron;
 *  - desfacuta, lista compacta, cele mai noi intai, cu Assign pe fiecare -
 *    ACEEASI fereastra ca butonul din celula de proprietar (`AssignModal`,
 *    fisa `UnassignedCard`), nu o a doua cale;
 *  - propunerea "Assign to <om>" cand userul logat se potriveste cu O fisa de
 *    persoana a firmei: se aplica numai la clic, prin capatul fisei aparatului
 *    (`POST /devices/{id}/owner`, `DeviceSummary::assignOwner`);
 *  - insigna rosie din antet (`badge`) deschide banda si deruleaza la ea.
 *
 * Datele vin cu pagina listei (`TablePage.unassigned`), deci banda numara
 * exact scopul listei: firma, sediul, filtrele, cautarea. Dreptul de Assign e
 * `tally.canAssign` (`overview.unassigned_details_assign.read`), acelasi ca la
 * fisa si la butonul din celula.
 */
export function useUnassignedBand(
  data: UnassignedBandData | null | undefined,
  tally: UnassignedTally | undefined,
  onAssigned: () => void,
) {
  const [open, setOpen] = useState(false)
  const [assignId, setAssignId] = useState<number | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [busy, setBusy] = useState<number | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const queries = useQueryClient()
  const canAssign = tally?.canAssign === true
  const count = data?.count ?? 0

  const done = () => {
    setModalOpen(false)
    void queries.invalidateQueries({ queryKey: unassignedTallyQuery.queryKey })
    onAssigned()
  }

  const applySuggestion = async (deviceId: number, ownerId: number) => {
    setBusy(deviceId)
    try {
      await assignOwner(deviceId, ownerId)
      toast('Device assigned', 'Assign')
      done()
    } catch (e) {
      toast((e as Error).message || 'The device could not be assigned.', 'Assign', 'error')
    } finally {
      setBusy(null)
    }
  }

  if (!data || count < 1) return { badge: null, band: null }

  const newest = stamp(data.lastDiscovered)
  const label = count === 1 ? '1 station without a user' : `${count} stations without a user`

  const badge = (
    <a
      className="btn od-unknown-badge tk-header-extra"
      href="#od-unknown-band"
      title="Stations without a user - click to see them"
      onClick={(e) => {
        e.preventDefault()
        setOpen(true)
        ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      }}
    >
      <i className="fas fa-user-slash" aria-hidden="true"></i> {count}
    </a>
  )

  const band = (
    <div className={`od-unknown-band${open ? ' is-open' : ''}`} id="od-unknown-band" ref={ref}>
      <button type="button" className="od-unknown-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <i className="fas fa-user-slash" aria-hidden="true"></i>
        <strong className="od-unknown-count">{label}</strong>
        {newest ? (
          <span className="od-unknown-last" title={exact(newest)}>
            last discovered {fromNow(newest)}
          </span>
        ) : null}
        {data.adAssign ? <span className="od-unknown-ad">AD assignment is on - these could not be matched</span> : null}
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} od-unknown-chevron`} aria-hidden="true"></i>
      </button>
      {open ? (
        <div className="od-unknown-body">
          <table className="table table-condensed od-unknown-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Type</th>
                <th>Office</th>
                <th>IP</th>
                <th>Last logged-in user</th>
                <th>Discovered</th>
                <th>Last contact</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.devices.map((d) => {
                const added = stamp(d.addedAt)
                const seen = stamp(d.lastContactAt)
                return (
                  <tr key={d.id} data-id={d.id}>
                    <td>
                      <a href={`/app/inventoree/device/${d.id}`}>{d.name}</a>
                    </td>
                    <td>{d.type}</td>
                    <td>{d.office}</td>
                    <td>{d.ip}</td>
                    <td>{d.loggedUser}</td>
                    <td title={added ? exact(added) : undefined}>{added ? fromNow(added) : ''}</td>
                    <td title={seen ? exact(seen) : undefined}>{seen ? fromNow(seen) : ''}</td>
                    <td className="od-unknown-actions">
                      {canAssign && d.suggestion ? (
                        <button
                          type="button"
                          className="btn btn-xs btn-default od-unknown-suggest"
                          disabled={busy === d.id}
                          title={`Suggested from the last logged-in user (${d.loggedUser})`}
                          onClick={() => void applySuggestion(d.id, d.suggestion!.id)}
                        >
                          <i className="fas fa-magic" aria-hidden="true"></i> Assign to {d.suggestion.name}
                        </button>
                      ) : null}
                      {canAssign ? (
                        <button
                          type="button"
                          className="btn btn-xs btn-theme od-unknown-assign"
                          onClick={() => {
                            setAssignId(d.id)
                            setModalOpen(true)
                          }}
                        >
                          <i className="fas fa-user-check" aria-hidden="true"></i> Assign
                        </button>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {count > data.devices.length ? (
            <div className="od-unknown-more">
              Showing the {data.devices.length} newest of {count}.{' '}
              {tally?.canList ? <a href="/app/inventoree/unassigned">See all in Unknown</a> : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {canAssign && assignId !== null ? (
        <AssignModal domId={`odUnknownBandModal-${assignId}`} id={assignId} open={modalOpen} onClose={() => setModalOpen(false)} onAssigned={done} />
      ) : null}
    </div>
  )

  return { badge, band }
}
